import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, Calculator, BarChart3, AlertTriangle, Scale } from "lucide-react";
import EMICalculator from "../components/tools/EMICalculator";
import MhMFlow from "../components/tools/mhm/MhMFlow";
import ProjectComparator from "../components/tools/ProjectComparator";
import { API } from "../App";

const PublicToolPage = () => {
  const { token } = useParams();
  const [toolData, setToolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTool = async () => {
      try {
        const res = await axios.get(`${API}/tools/public/${token}`);
        setToolData(res.data);
      } catch (err) {
        if (err.response?.status === 410) {
          setError("expired");
        } else if (err.response?.status === 404) {
          setError("not_found");
        } else {
          setError("unknown");
        }
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchTool();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-slate-500 text-sm">Loading tool...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="public-tool-error">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {error === "expired" ? "Link Expired" : error === "not_found" ? "Link Not Found" : "Something Went Wrong"}
          </h2>
          <p className="text-slate-500 text-sm">
            {error === "expired"
              ? "This link has expired. Please ask your sales representative for a new link."
              : error === "not_found"
              ? "This link doesn't exist or has been deactivated."
              : "Please try again later or contact your sales representative."}
          </p>
        </div>
      </div>
    );
  }

  const renderTool = () => {
    if (toolData?.tool_type === "emi_calculator") {
      return (
        <EMICalculator
          apiBase={API}
          prefill={toolData.prefill_data}
          isPublicView={true}
          linkToken={token}
          showShareButton={false}
          compact={false}
        />
      );
    }
    if (toolData?.tool_type === "mhm") {
      return (
        <MhMFlow
          apiBase={API}
          linkToken={token}
          isPublicView={true}
        />
      );
    }
    if (toolData?.tool_type === "project_comparator") {
      return (
        <ProjectComparator
          apiBase={API}
          prefill={toolData.prefill_data}
          isPublicView={true}
          linkToken={token}
          showShareButton={false}
        />
      );
    }
    return <p className="text-center text-slate-500">Unsupported tool type.</p>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="public-tool-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {toolData?.project_name || "Property Tools"}
              </p>
              {toolData?.created_by_name && (
                <p className="text-xs text-slate-500">
                  Prepared by {toolData.created_by_name}
                  {toolData.lead_name ? ` for ${toolData.lead_name}` : ""}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-blue-600">
            {toolData?.tool_type === "mhm" ? (
              <><BarChart3 className="w-4 h-4" /><span className="text-sm font-medium">MyHomeMatrix</span></>
            ) : toolData?.tool_type === "project_comparator" ? (
              <><Scale className="w-4 h-4" /><span className="text-sm font-medium">Project Comparator</span></>
            ) : (
              <><Calculator className="w-4 h-4" /><span className="text-sm font-medium">EMI Calculator</span></>
            )}
          </div>
        </div>
      </div>

      {/* Tool Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {toolData?.lead_name && (
          <div className="mb-4 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Hi <span className="font-semibold">{toolData.lead_name}</span>, use this calculator to explore your financing options. Your inputs help us understand your preferences better.
            </p>
          </div>
        )}
        {renderTool()}
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 mt-8">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center">
          <p className="text-xs text-slate-400">
            Powered by PropertyCRM | This is a personalized tool link
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicToolPage;
